import { computed, defineComponent, onMounted, onUnmounted, onUpdated, reactive, ref } from 'vue';
import { Popover } from 'bootstrap';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { MinisterAction, MinisterGetter } from '@/store/minister/minister.module';
import { Minister } from '@/store/minister/minister.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import CabinetMinisterFormComponent from '@/components/cabinetDetail/cabinetMinisterForm/CabinetMinisterForm.vue';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';
import LoadingComponent from '@/components/loading/Loading.vue';
import Draggable from 'vuedraggable';

export default defineComponent({
  name: 'CabinetMinisterListComponent',
  components: {
    CabinetMinisterFormComponent,
    PerfectScrollbar,
    LoadingComponent,
    Draggable,
  },
  props: {
    cabinetId: {
      type: Number,
    },
    cabinetStatus: {
      type: Number,
    },
  },
  setup: (props) => {
    const store = useStore();
    const toast = useToast();
    const cabinetMinisters = computed<CabinetMinister[]>(() => store.getters[CabinetMinisterGetter.ALL]);
    const ministers = computed<Minister[]>(() => {
      const all = store.getters[MinisterGetter.ALL] as Minister[];
      return all.filter(m => cabinetMinisters.value.every(x => x.minister.id !== m.id));
    });
    const search = computed(() => ministers.value.filter((minister) => toSearch(minister)));
    const isLoading = computed(() => store.getters[CabinetMinisterGetter.STATUS] === 'init' || store.getters[CabinetMinisterGetter.STATUS] === 'loading');
    const isDrag = ref(false);
    const dragElement = ref(0);
    const popoverList = ref<Popover[]>([]);

    const dragging = ref(false);
    const enabled = props.cabinetStatus === 0;
    const dragOptions = {
      animation: 200,
      group: 'description',
    };

    const cabinetMinisterForm = ref<InstanceType<typeof CabinetMinisterFormComponent>>();
    const selectedCabinetMinister = reactive<{
      cabinetId: number,
      ministerId?: number | null,
      ministerPositionId?: number | null,
      regions: string[],
    }>({
      cabinetId: props.cabinetId as number,
      ministerId: null,
      ministerPositionId: null,
      regions: []
    });

    const filter = reactive({
      keyword: '',
      amount: computed(() => search.value.length),
      total: computed(() => ministers.value.length)
    });

    onMounted(() => {
      store.dispatch(CabinetMinisterAction.FETCH, props.cabinetId);
      store.dispatch(MinisterAction.FETCH)
        .then(() => initialPopover());
    });

    // popover
    onUpdated(() => initialPopover());
    onUnmounted(() => {
      popoverList.value.map(element => element.dispose());
    });

    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-minister"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'left', html: true }));
    };

    const onDragStart = (event: DragEvent, ministerId: string) => {
      isDrag.value = true;
      dragElement.value = parseInt(ministerId);
      event.dataTransfer?.setData('ministerId', ministerId);
    };

    const onDragEnd = () => {
      isDrag.value = false;
      dragElement.value = 0;
    };

    const onDrop = (event: DragEvent) => {
      const ministerId = parseInt(event.dataTransfer?.getData('ministerId') as string);

      if (ministerId) {
        if (!cabinetMinisters.value.some(m => m.minister.id === ministerId)) {
          Promise.resolve(selectedCabinetMinister.ministerId = ministerId)
            .then(() => cabinetMinisterForm.value?.show());
        } else {
          toast.warning('มีข้อมูล ร.นรม./รมต.นร. แล้ว');
        }
      }
    };

    interface VueDraggableEvent {
      moved: {
        element: unknown;
        oldIndex: number;
        newIndex: number;
      };
    }
    const onMoved = (event: VueDraggableEvent) => {
      const cabimentMinister = event.moved.element as CabinetMinister;

      store.dispatch(CabinetMinisterAction.REORDER, {
        cabinetId: props.cabinetId,
        ministerId: cabimentMinister.minister.id,
        sequence: event.moved.newIndex
      });
    };

    const editClick = (ministerId: number) => {
      if (props.cabinetStatus === 0) {
        new Promise<void>((resolve) => {
          const cabinetMinister = cabinetMinisters.value.find(m => m.id === ministerId) as CabinetMinister;
          selectedCabinetMinister.ministerId = cabinetMinister.minister.id;
          selectedCabinetMinister.ministerPositionId = cabinetMinister.position.id;
          selectedCabinetMinister.regions = cabinetMinister.regions.map(m => m.name);
          resolve();
        }).then(() => cabinetMinisterForm.value?.show());
      }
    };

    const clearSelectedCabinetMinister = () => {
      selectedCabinetMinister.ministerId = null;
      selectedCabinetMinister.ministerPositionId = null;
      selectedCabinetMinister.regions =  [];
    };

    const toSearch = (minister: Minister): Minister | boolean => {
      return (filter.keyword)
        ? minister.fullName.toLowerCase().includes(filter.keyword.toLowerCase())
          ? minister
          : false
        : true;
    };

    const popPhoto = (photo: string) => {
      return `<img src="${photo}" alt="photo" />`;
    };

    return {
      cabinetMinisters,
      search,
      filter,
      isLoading,
      isDrag,
      dragElement,
      cabinetMinisterForm,
      selectedCabinetMinister,
      dragging,
      enabled,
      dragOptions,
      onDragStart,
      onDragEnd,
      onDrop,
      onMoved,
      editClick,
      clearSelectedCabinetMinister,
      popPhoto,
    };
  }
});
